import type { BeforeRequestHook } from 'ky';
import { HTTP_HEADERS } from '../../../../auth/const';
import { isDpopAuthEnabled } from '../../../../dpop/dpopAuthEnabled';
import { getDpopHeaderValue } from '../../../../dpop/getDpopHeaderValue';
import { readDpopKeyPair } from '../../../../dpop/store';
import type { FetchOptions } from '../../types';
import { isServer } from '../../utils';
import type { HookGetterArgs } from '../types';

function toOrigin(value: string): string | null {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

// origin only: a DPoP header on a CDN or customer origin leaks the key and trips CORS
export function shouldSignRequest(url: URL, options?: FetchOptions): boolean {
  if (typeof window !== 'undefined') {
    if (url.origin === window.location.origin) return true;

    const baseUrl = (window as Window & { __unifyBaseUrl?: string }).__unifyBaseUrl;
    if (baseUrl && url.origin === toOrigin(baseUrl)) return true;
  }

  const apiDomain = options?.meta?.apiDomain;
  return Boolean(apiDomain && url.origin === toOrigin(apiDomain));
}

/**
 * `/auth/*` verifies no proof: login and MFA carry the public key in the BODY as `dpopPublicKey`,
 * and cross-origin the header is worse than useless — those routes answer a CORS allow-list
 * without `DPoP`, so the preflight fails and the request is never made.
 *
 * `/auth/` is a SERVICE prefix, not a path inside the api service (`INTERNAL_SVC_HOST` in
 * ./internalSvc.ts routes it by first segment), so the public-page rewrite of `/api/x` to `/auth/x`
 * targets the auth service's own session-free twin — not a proof-verified route in disguise.
 *
 * Matched the way the gateway matches, first segment after the basePath: a bare `includes` would
 * unsign an app whose own id is `auth`, a plain prefix would miss `/t/<tenant>/auth/login`.
 */
function isAuthService(url: URL, options?: FetchOptions): boolean {
  const rawBasePath = options?.meta?.basePath;
  const basePath = rawBasePath && !rawBasePath.startsWith('/') ? `/${rawBasePath}` : rawBasePath;
  const path =
    basePath && url.pathname.startsWith(basePath)
      ? url.pathname.slice(basePath.length)
      : url.pathname;
  return path.startsWith('/auth/');
}

// returns the request so getInterfaceHeaders can sign and return in one step
export const addDpopHeader = async (request: Request, options: FetchOptions): Promise<Request> => {
  // re-checked here so a pair stored while the release was opted in stops signing too
  if (!isDpopAuthEnabled()) return request;
  // the server holds no key; the backend skips server calls by their `s_` trace id
  if (isServer()) return request;

  try {
    const url = new URL(request.url);
    if (!shouldSignRequest(url, options)) return request;
    if (isAuthService(url, options)) return request;

    const keyPair = options.dpopKeyPair ?? (await readDpopKeyPair(url.origin));
    if (!keyPair) return request;

    const headerValue = await getDpopHeaderValue(keyPair, request.method, url);
    // set, not skip-if-present: ky's 401 retry needs a fresh jti or it reads as a replay
    if (headerValue) request.headers.set(HTTP_HEADERS.DPoP, headerValue);
  } catch (error) {
    // fail soft — signing must never be the reason a request fails
    console.warn('Failed to add the auth header:', error);
  }
  return request;
};

export const getDpopHeaders = ({ options }: HookGetterArgs): BeforeRequestHook => {
  const dpopHeadersHook: BeforeRequestHook = async (request) => {
    await addDpopHeader(request, options);
  };
  return dpopHeadersHook;
};
